/**
 * @ignore
 * Created by daletan on 8/25/16.
 */

'use strict';

const get = require('lodash.get');
const React = require('react');
const PropTypes = require('prop-types');
const Relay = require('react-relay/classic');

const { FormattedMessage, FormattedNumber } = require('react-intl');

// Stores
const ItemStore = require('../../stores/ItemStore.es');
const UserStore = require('../../../global/stores/UserStore.es');

const { OPEN_DETAILS_FROM_SIDEBAR } = require('../../constants/shippingConstants');

// Components
const VerifiedTradePromo = require('./VerifiedTradePromo/VerifiedTradePromo').default;
const QuotePrimary = require('./QuotePrimary');
const ShippingDetailsLink = require('./ShippingDetailsLink');
const ShippingIcon = require('dibs-vg/dist/react/shipping');

// Helpers
const provideContext = require('fluxible-addons-react/provideContext');
const connectToStores = require('fluxible-addons-react/connectToStores');
const { filterQuotes } = require('./shippingHelpers');
const copy = require('./Blurb.json');
const styles = require('../../../../scss/new-pdp/pdp-shipping-blurb.scss');

const ShippingBlurb = props => {
    const canShowBlurb = !(props.isSold
        || props.isUnlisted
        || props.hidePrice
        || !props.currency
        || !props.relayItem);
    const { user = null, conversation, quotes, currency, context, hasUserId,
        userId, itemId, dealerPk, viewer, dealerData } = props;

    const pdpBundleEnabled = get(viewer, 'pdpBundleEnabled');

    if (canShowBlurb) {
        const customQuote = pdpBundleEnabled && get(conversation, 'transaction.shipmentQuotes', []).find((quote) => quote.isCustom);
        const hasQuote = filterQuotes(quotes.domestic).length || filterQuotes(quotes.local).length || customQuote;
        const isVerifiedTrade = get(user, 'canSeeTradeReturns', false);
        const quoteRequestForAllIsEnabled = get(viewer, 'quoteRequestForAllIsEnabled');
        const canRequest = hasUserId && (quoteRequestForAllIsEnabled || isVerifiedTrade);
        const labelText = hasQuote ? copy.hasQuote : copy.noQuote;
        const link = (
            <a href={labelText.href}>
                {canRequest ? labelText.canRequest : labelText.cannotRequest}
            </a>
        );

        let customQuoteAmount;
        let customQuoteFormattedAmount;
        let customQuoteCopy;

        if (customQuote) {
            customQuoteAmount = get(customQuote, 'totalAmount.convertedAmountList').find(amount => amount.currency === currency);
            customQuoteFormattedAmount = (
                <FormattedNumber
                    value={customQuoteAmount.amount}
                    style='currency'
                    currency={currency}
                />
            );

            customQuoteCopy = (
                <FormattedMessage
                    id="pdp.Blurb.customQuote"
                    defaultMessage="{amount} Custom Quote"
                    values={{amount: customQuoteFormattedAmount}}
                />
            );
        }

        return (
            <div className="pdp-sidebar-icon pdp-shipping-blurb">
                <div className={`${styles.icon} shipping-blurb-icon`}>
                    <ShippingIcon />
                </div>
                <div className={`${styles.info} shipping-blurb-info`}>
                    <span className={styles.title}>Shipping: </span>
                    <div className={styles.quote}>
                        {customQuote && <div>{customQuoteCopy}</div>}
                        {!customQuote && <QuotePrimary currency={currency} quotes={quotes} customQuote={customQuote} />}
                    </div>
                    <div className={styles.promo}>
                        <VerifiedTradePromo
                            hasUserId={hasUserId}
                            userId={userId}
                            itemId={itemId}
                            viewer={viewer}
                        />
                    </div>
                    {!props.isOnHold && (
                        <div className={`${styles.link} shipping-blurb-link`}>
                            <ShippingDetailsLink
                                hasUserId={hasUserId}
                                userId={userId}
                                itemId={itemId}
                                dealerPk={dealerPk}
                                viewer={viewer}
                                link={link}
                                context={context}
                                user={user}
                                quotes={quotes}
                                parentName={'blurb'}
                                customQuoteAvailable={hasUserId}
                                regionType="DOMESTIC"
                                source={OPEN_DETAILS_FROM_SIDEBAR}
                                dealerData={dealerData}
                                trackingLocation="pdp sidebar"
                                item={props.relayItem}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }
    return <div className="pdp-shipping-blurb no-data" />;
};

ShippingBlurb.propTypes = {
    context: PropTypes.object,
    conversation: PropTypes.object,
    quotes: PropTypes.object.isRequired,
    user: PropTypes.object,
    hasUserId: PropTypes.bool,
    userId: PropTypes.string,
    itemId: PropTypes.string,
    dealerPk: PropTypes.string,
    viewer: PropTypes.object,
    relayItem: PropTypes.object,
    currency: PropTypes.string.isRequired,
    isOnHold: PropTypes.bool,
    isSold: PropTypes.bool,
    isUnlisted: PropTypes.bool,
    hidePrice: PropTypes.bool,
    dealerData: PropTypes.object,
    pagePosition: PropTypes.string
};

module.exports = Relay.createContainer(provideContext(connectToStores(
    ShippingBlurb,
    [
        ItemStore,
        UserStore
    ],
    context => {
        const userStore = context.getStore(UserStore);
        const itemStore = context.getStore(ItemStore);
        return ({
            currency: userStore.getCurrency(),
            isOnHold: itemStore.getIsOnHold(),
            isSold: itemStore.getIsSold(),
            isUnlisted: itemStore.getIsUnlisted(),
            hidePrice: itemStore.getHidePrice()
        });
    }
)), {
    initialVariables: {
        hasUserId: false,
        userId: null,
        itemId: null
    },
    fragments: {
        user: ({hasUserId, itemId, userId}) => Relay.QL`
            fragment on User {
                canSeeTradeReturns: hasPermission(name: "B_TRADE_RETURN_POLICY")
                ${ShippingDetailsLink.getFragment('user', { hasUserId, itemId, userId })}
            }
        `,
        relayItem: ({ hasUserId, itemId, userId }) => Relay.QL`
            fragment on Item {
                ${ShippingDetailsLink.getFragment('item', { hasUserId, itemId, userId })}
            }
        `,
        viewer: ({ hasUserId, itemId, userId }) => Relay.QL`
            fragment on Viewer {
                quoteRequestForAllIsEnabled: isEnabled(key: "enableRequestQuoteForAllLoggedIn")
                pdpBundleEnabled: isEnabled(key: "pdpBundle")
                ${ShippingDetailsLink.getFragment('viewer', { hasUserId, itemId, userId })}
                ${VerifiedTradePromo.getFragment('viewer', { hasUserId, itemId, userId })}
            }
        `,
        conversation: () => Relay.QL`
            fragment on Conversation {
                transaction {
                    shipmentQuotes {
                        isCustom
                        totalAmount {
                            convertedAmountList {
                                amount
                                currency
                            }
                        }
                    }
                }
            }
        `
    }
});



// WEBPACK FOOTER //
// ./assets/js/newPdp/components/Shipping/Blurb.jsx